import React from "react";
import { isEmpty } from "lodash";
import type { ApolloClient } from "@apollo/client";
import { Modal } from "react-bootstrap";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { noop, get } from "lodash";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { contributionConstants, accountConstants } from "actions/types";
import { getAllAccounts } from "actions/accountActions";
import ContributionSetup from "./ContributionSetup";
import {
  getContributionRecommendations,
  createRecurringContribution,
  getContributionAbilityAndEstimates,
} from "actions/contributionActions";
import IconMessageCard from "components/IconMessageCard";
import { FiCheck } from "react-icons/fi";
import { recurringContributionSelector } from "store/selectors/contribution";

import "./RecurringContributionModal.scss";
import {
  activeInvestmentAccountsSelector,
  recurringContributionAccountIdSelector,
} from "store/selectors/accounts";
import { getIraAccountIdSelector } from "store/selectors/user";
import type { RootState } from "types/state";

interface RecommendedContribution {
  recommended?: number;
  other?: number[];
  isMaxContributionOption?: boolean;
}

/** Shape of the recurring schedule held in state / passed to ContributionSetup. */
interface RecurringSchedule {
  frequency?: string;
  dayOne?: string;
  anchorPayDate?: string;
}

const actions = [
  contributionConstants.ADD_RECURRING_CONTRIBUTION,
  contributionConstants.GET_CONTRIBUTION_RECOMMENDATIONS,
  accountConstants.GET_ACCOUNTS,
];

const errorSelector = createErrorSelector(actions);
const loadingSelector = createLoadingSelector(
  contributionConstants.GET_CONTRIBUTION_RECOMMENDATIONS
);
const isSubmitting = createLoadingSelector(actions);

const mapStateToProps = (state: RootState) => {
  const {
    dailyMax,
    weeklyMax,
    monthlyMax,
    everyOtherWeekMax,
    recommendations,
  } = state.contribution;
  // contribution.recommendations is typed `object`; the daily/weekly/etc.
  // buckets are read off it here.
  const recs = recommendations as {
    daily?: RecommendedContribution;
    weekly?: RecommendedContribution;
    everyOtherWeek?: RecommendedContribution;
    monthly?: RecommendedContribution;
  };

  return {
    dailyMax,
    weeklyMax,
    monthlyMax,
    everyOtherWeekMax,
    dailyRecommendations: recs.daily,
    weeklyRecommendations: recs.weekly,
    everyOtherWeekRecommendations: recs.everyOtherWeek,
    monthlyRecommendations: recs.monthly,
    error: errorSelector(state),
    isFetching: loadingSelector(state),
    isSubmitting: isSubmitting(state),
    accounts: activeInvestmentAccountsSelector(state),
    recurringContribution: recurringContributionSelector(state),
    recurringContributionAccountId:
      recurringContributionAccountIdSelector(state),
    accountId: getIraAccountIdSelector(state),
  };
};

const mapDispatchToProps = {
  getContributionRecommendations,
  createRecurringContribution,
  getAllAccounts,

  getContributionAbilityAndEstimates,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  onClose?: () => void;
  show?: boolean;
  currentContributionAmount?: number;
  bankId?: string;
  scheduleId?: string;
  bankAccountNumber?: string;
  contributionSource?: {
    bankName?: string | null;
    accountSubtype?: string | null;
    accountId?: string | null;
    accountType?: string | null;
  };
  onSuccess?: () => void;
  // Dead passes from ContributionSource: forwarded by the parent but never read
  // here (kept for runtime parity). Typed loosely so the parent's shapes are
  // accepted without changing behavior. `recurringContribution` is omitted on
  // purpose: it is injected by mapStateToProps, so connect drops it from the
  // accepted external props; the parent's dead pass is preserved behind an
  // attribute-level @ts-expect-error at the call site.
  bankAccount?: object;
  accountType?: string | null;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface State {
  activeStep: number;
  recurringContribution: RecurringSchedule;
  showContributionSelector: boolean;
  submittedContribution: boolean;
}

class RecurringContributionModal extends React.PureComponent<Props, State> {
  static getDerivedStateFromProps(
    nextProps: Props,
    prevState: State
  ): Partial<State> {
    const newState: Partial<State> = {};

    if (
      isEmpty(prevState.recurringContribution) &&
      !isEmpty(nextProps.recurringContribution)
    ) {
      // recurringContributionSelector yields a RecurringContributionSchedule
      // (dayOne is an Int); cast to the schedule shape ContributionSetup reads.
      newState.recurringContribution =
        nextProps.recurringContribution as RecurringSchedule;
    }

    return newState;
  }
  // PRESERVED BUG (conversion-only): original constructor calls super() with no
  // props instead of super(props); preserved verbatim.
  constructor() {
    // @ts-expect-error -- super() called without props to match original runtime behavior
    super();

    this.state = {
      activeStep: 0,
      recurringContribution: {},
      showContributionSelector: false,
      submittedContribution: false,
    };
  }
  componentDidMount() {
    void this.props.getContributionRecommendations(
      this.props.client as ApolloClient<object>,
      {
        isSelfEmployed: true,
      }
    );
  }

  _submitContribution = async (
    amount: number,
    isDailyCustom: boolean,
    schedule: {
      frequency: string;
      dayOne: number | undefined;
      anchorPayDate: string;
    },
    accountId: string
  ) => {
    try {
      await this.props.createRecurringContribution(
        this.props.client as ApolloClient<object>,
        {
          accountId,
          scheduleId: this.props.scheduleId,
          dayOne: schedule.dayOne,
          frequency: schedule.frequency,
          anchorPayDate: schedule.anchorPayDate,
          // bankId is an optional OwnProp but the mutation requires it; parent
          // always supplies it at runtime.
          bankId: this.props.bankId as string,
          isDailyCustom,
          amount: +amount,
        }
      );

      if (!this.props.error) {
        this.setState({ submittedContribution: true });
        void this.props.getContributionAbilityAndEstimates(
          this.props.client as ApolloClient<object>
        );
      }
    } catch (error) {
      return error;
    }
  };

  _successContent = () => {
    const header = "Your scheduled contribution has been set up successfully.";
    const actionText = "Close";

    return (
      <>
        <IconMessageCard
          header={header}
          icon={<FiCheck color="white" stroke="#60A4BF" size={16} />}
          actionText={actionText}
          onClick={() => {
            // PRESERVED BUG (conversion-only): onClose is optional but invoked
            // unconditionally; ! preserves the original throw-if-undefined.
            this.props.onClose!();
          }}
        />
      </>
    );
  };

  _getModalBody = () => {
    const bankName = get(this.props, "contributionSource.bankName", "");
    const accountSubtype = get(
      this.props,
      "contributionSource.accountSubtype",
      ""
    );
    const lastFourOfAccount = get(
      this.props,
      "contributionSource.accountId",
      ""
    );
    const accountType = get(this.props, "contributionSource.accountType", "");
    const isIraAccount = ["TRADITIONAL_IRA", "ROTH_IRA"].includes(accountType);

    if (this.state.submittedContribution) {
      return this._successContent();
    }

    return (
      <>
        <ContributionSetup
          schedule={this.state.recurringContribution}
          currentContributionAmount={this.props.currentContributionAmount}
          weeklyMax={this.props.weeklyMax}
          monthlyMax={this.props.monthlyMax}
          everyOtherWeekMax={this.props.everyOtherWeekMax}
          everyOtherWeekRecommendations={
            this.props.everyOtherWeekRecommendations
          }
          onSubmit={({ schedule, amount, isDailyCustom, accountId }) => {
            void this._submitContribution(
              amount,
              isDailyCustom,
              schedule,
              accountId
            );
          }}
          error={this.props.error}
          isSubmitting={this.props.isSubmitting}
          onClose={this.props.onClose}
          fromBankName={bankName}
          fromAccountName={accountSubtype}
          fromLastFourOfAccount={lastFourOfAccount}
          isFromBank={!isIraAccount}
          // IraAccount[] (id is a nullable ID) → ContributionSetup's
          // { id?: string; accountType?: string }[] account shape.
          accounts={
            this.props.accounts as Array<{
              id?: string;
              accountType?: string;
            }>
          }
          defaultAccountId={
            (this.props.recurringContributionAccountId ||
              this.props.accountId) as string | undefined
          }
        />
      </>
    );
  };

  render() {
    return (
      <Modal
        centered
        scrollable
        show={this.props.show}
        onHide={noop}
        className="contribution-modal"
      >
        <Modal.Body>{this._getModalBody()}</Modal.Body>
      </Modal>
    );
  }
}

export default connector(withApollo<Props>(RecurringContributionModal));
